import { call, put, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/alert.slice';
import * as api from '@/services/alert';
import { PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import { IApiResponse, IFetchAlerts } from '@/services/alert/alerts.type';

// Define action types
export interface GetAlertsAction extends PayloadAction<IFetchAlerts> {
  type: "alerts/getAlerts"
}

export interface GetAlertByIdAction extends PayloadAction<string> {
  type: "alerts/getAlertById"
}

// 1. Handler for fetching the list of alerts
function* getAlerts(action: GetAlertsAction): Generator {
  try {
    const response: any = yield call(api.fetchAlerts, action?.payload);
    const responseData: IApiResponse = response?.data;
    if (responseData && isBoolean(responseData.success) && responseData.success) {
      yield put(actions.getAlertsSuccess(responseData));
    } else {
      yield put(actions.getAlertsFailure(responseData || { message: "Failed to fetch alerts" }));
    }
  } catch (error: any) {
    const errorData = error?.response?.data || {
      message: error?.message || "An unexpected error occurred",
      statusCode: error?.response?.status || 500
    };
    yield put(actions.getAlertsFailure(errorData));
  }
}

// 2. Handler for fetching a single alert by ID
function* getAlertById(action: GetAlertByIdAction): Generator {
  try {
    const response: any = yield call(api.fetchAlertById, action?.payload);
    const responseData: any = response?.data;
    if (responseData && responseData.success !== false && (responseData.alert?.id || responseData.providerId)) {
      yield put(actions.getAlertByIdSuccess({
        success: true,
        message: responseData.message || "Successfully fetched alert",
        data: responseData
      }));
    } else {
      // Pass the responseData payload to the Failure action
      yield put(actions.getAlertByIdFailure(responseData || { message: "Alert not found" }));
    }
  } catch (error: any) {
    const apiResponse = error?.response?.data;

    const errorData = {
      message: apiResponse?.message || error?.message || "An unexpected error occurred",
      statusCode: apiResponse?.statusCode || "",
      messageDetails: apiResponse?.messageDetails || [],
    };

    yield put(actions.getAlertByIdFailure(errorData));
  }
}

function* alertSaga(): Generator {
  yield takeLatest("alerts/getAlerts", getAlerts);
  yield takeLatest("alerts/getAlertById", getAlertById);
}

export default alertSaga;
